import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiPlus, FiRefreshCw, FiSave, FiTrash2 } from 'react-icons/fi';
import api from '../services/api';
import '../components/Button.css';

const emptyTier = () => ({ groep: '', max_liter: '', max_gewicht_kg: '', tarief_per_dag: '' });
const emptyTarief2Tier = () => ({ groep: '', min_liter: '', max_liter: '', tarief_per_week: '' });

const formatEuro = (n) => {
  if (n == null || Number.isNaN(n)) return '–';
  return `€ ${Number(n).toFixed(4).replace('.', ',')}`;
};

const formatEuro2 = (n) => {
  if (n == null || Number.isNaN(n)) return '–';
  return `€ ${Number(n).toFixed(2).replace('.', ',')}`;
};

const vergelijkingLabel = (v) => {
  if (!v?.goedkoopste) return '–';
  if (v.goedkoopste === 'gelijk') return 'Gelijk';
  return v.goedkoopste_naam || (v.goedkoopste === 'tarief_1' ? 'Heeren' : 'AG');
};

const ProductenOpslagTab = ({ searchTerm = '' }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingTarief2, setSavingTarief2] = useState(false);
  const [tarief1Naam, setTarief1Naam] = useState('');
  const [tarief2Naam, setTarief2Naam] = useState('');
  const [kortingPct, setKortingPct] = useState('5');
  const [tiers, setTiers] = useState([]);
  const [tarief2Tiers, setTarief2Tiers] = useState([]);
  const [productRows, setProductRows] = useState([]);
  const [samenvatting, setSamenvatting] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/opslag-tarieven/producten');
      const settings = res.data?.tarief_1 || res.data?.settings || {};
      const tarief2Settings = res.data?.tarief_2 || res.data?.pick_settings || {};
      setTarief1Naam(settings.naam || 'Heeren');
      setTarief2Naam(tarief2Settings.naam || 'AG');
      setKortingPct(String(settings.korting_pct ?? 0));
      setTiers(
        (settings.tiers || []).map((t) => ({
          groep: t.groep || '',
          max_liter: t.max_liter == null ? '' : String(t.max_liter),
          max_gewicht_kg: t.max_gewicht_kg == null ? '' : String(t.max_gewicht_kg),
          tarief_per_dag: String(t.tarief_per_dag ?? ''),
        }))
      );
      setTarief2Tiers(
        (tarief2Settings.tiers || []).map((t) => ({
          groep: t.groep || '',
          min_liter: t.min_liter == null ? '' : String(t.min_liter),
          max_liter: t.max_liter == null ? '' : String(t.max_liter),
          tarief_per_week: String(t.tarief_per_week ?? ''),
        }))
      );
      setProductRows(res.data?.producten || []);
      setSamenvatting(res.data?.samenvatting || null);
    } catch (error) {
      console.error('Opslagtarieven laden mislukt:', error);
      alert('Fout bij laden: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveTiers = async () => {
    setSaving(true);
    try {
      const payload = {
        naam: (tarief1Naam || '').trim() || 'Heeren',
        korting_pct: parseFloat(String(kortingPct).replace(',', '.')) || 0,
        tiers: tiers.map((t, i) => ({
          groep: (t.groep || String.fromCharCode(65 + i)).trim(),
          max_liter: t.max_liter === '' || t.max_liter == null ? null : parseFloat(String(t.max_liter).replace(',', '.')),
          max_gewicht_kg:
            t.max_gewicht_kg === '' || t.max_gewicht_kg == null
              ? null
              : parseFloat(String(t.max_gewicht_kg).replace(',', '.')),
          tarief_per_dag: parseFloat(String(t.tarief_per_dag).replace(',', '.')) || 0,
        })),
      };
      await api.put('/opslag-tarieven', payload);
      await fetchData();
      alert('Heeren-tarieven opgeslagen');
    } catch (error) {
      alert('Fout bij opslaan: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTarief2Tiers = async () => {
    setSavingTarief2(true);
    try {
      const payload = {
        naam: (tarief2Naam || '').trim() || 'AG',
        tiers: tarief2Tiers.map((t) => ({
          groep: (t.groep || '').trim(),
          min_liter: parseFloat(String(t.min_liter).replace(',', '.')) || 0,
          max_liter:
            t.max_liter === '' || t.max_liter == null
              ? null
              : parseFloat(String(t.max_liter).replace(',', '.')),
          tarief_per_week: parseFloat(String(t.tarief_per_week).replace(',', '.')) || 0,
        })),
      };
      await api.put('/opslag-tarieven/pick', payload);
      await fetchData();
      alert('AG-tarieven opgeslagen');
    } catch (error) {
      alert('Fout bij opslaan: ' + (error.response?.data?.error || error.message));
    } finally {
      setSavingTarief2(false);
    }
  };

  const updateTier = (index, field, value) => {
    setTiers((prev) => prev.map((t, i) => (i === index ? { ...t, [field]: value } : t)));
  };

  const updateTarief2Tier = (index, field, value) => {
    setTarief2Tiers((prev) => prev.map((t, i) => (i === index ? { ...t, [field]: value } : t)));
  };

  const addTier = () => setTiers((prev) => [...prev, emptyTier()]);
  const removeTier = (index) => setTiers((prev) => prev.filter((_, i) => i !== index));
  const addTarief2Tier = () => setTarief2Tiers((prev) => [...prev, emptyTarief2Tier()]);
  const removeTarief2Tier = (index) => setTarief2Tiers((prev) => prev.filter((_, i) => i !== index));

  const label1 = samenvatting?.tarief_1_naam || tarief1Naam || 'Heeren';
  const label2 = samenvatting?.tarief_2_naam || tarief2Naam || 'AG';

  const filteredRows = useMemo(() => {
    const q = (searchTerm || '').trim().toLowerCase();
    if (!q) return productRows;
    return productRows.filter(
      (r) =>
        (r.naam || '').toLowerCase().includes(q) ||
        (r.sku || '').toLowerCase().includes(q)
    );
  }, [productRows, searchTerm]);

  if (loading) {
    return <div className="loading">Opslagkosten laden…</div>;
  }

  return (
    <div className="producten-opslag-tab">
      <p className="opslag-intro">
        Zelfde pickvoorraad (LVB): vergelijk de liter-tarieven van <strong>Heeren</strong> (huidige aanbieder) met <strong>AG</strong> (nieuw).
        Per product hetzelfde volume en dezelfde <strong>magazijnvoorraad</strong> — je ziet het effect op totale opslagkosten.
      </p>

      <section className="opslag-config-section">
        <h2>{label1} — literprijzen</h2>
        <div className="opslag-korting-row">
          <label htmlFor="tarief-1-naam">Naam aanbieder</label>
          <input
            id="tarief-1-naam"
            type="text"
            value={tarief1Naam}
            onChange={(e) => setTarief1Naam(e.target.value)}
            className="form-input opslag-naam-input"
          />
        </div>
        <p className="opslag-help">
          Huidige aanbieder: <strong>€/dag per stuk</strong> (Bol LVB-logica). Hoogste groep op volume (liter) of gewicht (kg).
          Volume uit <strong>Inhoud (L)</strong> of <strong>CBM × 1000</strong>.
        </p>

        <div className="opslag-korting-row">
          <label htmlFor="opslag-korting">Korting op tarieven (%)</label>
          <input
            id="opslag-korting"
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={kortingPct}
            onChange={(e) => setKortingPct(e.target.value)}
            className="form-input opslag-korting-input"
          />
        </div>

        <div className="table-container">
          <table className="data-table opslag-tier-table">
            <thead>
              <tr>
                <th>Groep</th>
                <th>Max. liter (tot)</th>
                <th>Max. kg (tot)</th>
                <th>Tarief / dag / stuk</th>
                <th aria-label="Acties" />
              </tr>
            </thead>
            <tbody>
              {tiers.map((tier, index) => (
                <tr key={index}>
                  <td>
                    <input
                      type="text"
                      value={tier.groep}
                      onChange={(e) => updateTier(index, 'groep', e.target.value)}
                      placeholder={String.fromCharCode(65 + index)}
                      className="form-input opslag-tier-input"
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={tier.max_liter}
                      onChange={(e) => updateTier(index, 'max_liter', e.target.value)}
                      placeholder={index === tiers.length - 1 ? '∞ (leeg)' : 'bijv. 6'}
                      className="form-input opslag-tier-input"
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={tier.max_gewicht_kg ?? ''}
                      onChange={(e) => updateTier(index, 'max_gewicht_kg', e.target.value)}
                      placeholder="kg"
                      className="form-input opslag-tier-input"
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={tier.tarief_per_dag}
                      onChange={(e) => updateTier(index, 'tarief_per_dag', e.target.value)}
                      placeholder="0,004"
                      className="form-input opslag-tier-input"
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn-icon"
                      onClick={() => removeTier(index)}
                      title="Verwijder rij"
                      disabled={tiers.length <= 1}
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="opslag-config-actions">
          <button type="button" className="btn btn-secondary" onClick={addTier}>
            <FiPlus /> Tariefgroep
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSaveTiers} disabled={saving}>
            <FiSave /> {saving ? 'Opslaan…' : `${label1} opslaan`}
          </button>
        </div>
      </section>

      <section className="opslag-config-section opslag-config-tarief-2">
        <h2>{label2} — literprijzen</h2>
        <div className="opslag-korting-row">
          <label htmlFor="tarief-2-naam">Naam aanbieder</label>
          <input
            id="tarief-2-naam"
            type="text"
            value={tarief2Naam}
            onChange={(e) => setTarief2Naam(e.target.value)}
            className="form-input opslag-naam-input"
          />
        </div>
        <p className="opslag-help">
          Nieuwe aanbieder AG: <strong>€/week per stuk</strong> per volume-groep (min/max liter).
          Maandtotalen: AG (week × 30/7) naast Heeren (dag × 30).
        </p>

        <div className="table-container">
          <table className="data-table opslag-tier-table">
            <thead>
              <tr>
                <th>Groep</th>
                <th>Min. liter</th>
                <th>Max. liter</th>
                <th>Tarief / week / stuk</th>
                <th aria-label="Acties" />
              </tr>
            </thead>
            <tbody>
              {tarief2Tiers.map((tier, index) => (
                <tr key={index}>
                  <td>
                    <input
                      type="text"
                      value={tier.groep}
                      onChange={(e) => updateTarief2Tier(index, 'groep', e.target.value)}
                      placeholder="XXS"
                      className="form-input opslag-tier-input"
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={tier.min_liter}
                      onChange={(e) => updateTarief2Tier(index, 'min_liter', e.target.value)}
                      className="form-input opslag-tier-input"
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={tier.max_liter}
                      onChange={(e) => updateTarief2Tier(index, 'max_liter', e.target.value)}
                      placeholder="leeg = ∞"
                      className="form-input opslag-tier-input"
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={tier.tarief_per_week}
                      onChange={(e) => updateTarief2Tier(index, 'tarief_per_week', e.target.value)}
                      placeholder="0,04"
                      className="form-input opslag-tier-input"
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn-icon"
                      onClick={() => removeTarief2Tier(index)}
                      title="Verwijder rij"
                      disabled={tarief2Tiers.length <= 1}
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="opslag-config-actions">
          <button type="button" className="btn btn-secondary" onClick={addTarief2Tier}>
            <FiPlus /> Tariefgroep
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSaveTarief2Tiers} disabled={savingTarief2}>
            <FiSave /> {savingTarief2 ? 'Opslaan…' : `${label2} opslaan`}
          </button>
          <button type="button" className="btn btn-secondary" onClick={fetchData}>
            <FiRefreshCw /> Vernieuwen
          </button>
        </div>
      </section>

      {samenvatting && (
        <div className="opslag-summary-cards">
          <div className="opslag-summary-card">
            <span className="opslag-summary-label">{label1} — totaal / dag</span>
            <strong>{formatEuro2(samenvatting.totaal_tarief_1_per_dag ?? samenvatting.totaal_opslag_per_dag)}</strong>
          </div>
          <div className="opslag-summary-card">
            <span className="opslag-summary-label">{label1} — totaal / maand</span>
            <strong>{formatEuro2(samenvatting.totaal_tarief_1_per_maand ?? samenvatting.totaal_opslag_per_maand)}</strong>
          </div>
          <div className="opslag-summary-card opslag-summary-tarief-2">
            <span className="opslag-summary-label">{label2} — totaal / week</span>
            <strong>{formatEuro2(samenvatting.totaal_tarief_2_per_week)}</strong>
          </div>
          <div className="opslag-summary-card opslag-summary-tarief-2">
            <span className="opslag-summary-label">{label2} — totaal / maand</span>
            <strong>{formatEuro2(samenvatting.totaal_tarief_2_per_maand)}</strong>
          </div>
          <div className="opslag-summary-card">
            <span className="opslag-summary-label">Lagere maandkosten ({label1} / {label2})</span>
            <strong>
              {samenvatting.goedkoper_tarief_1 ?? 0} / {samenvatting.goedkoper_tarief_2 ?? 0}
            </strong>
          </div>
          <div className="opslag-summary-card">
            <span className="opslag-summary-label">Producten met volume</span>
            <strong>
              {samenvatting.met_volume} / {samenvatting.aantal_producten}
            </strong>
          </div>
          {samenvatting.zonder_volume > 0 && (
            <div className="opslag-summary-card opslag-summary-warn">
              <span className="opslag-summary-label">Zonder volume (vul CBM of L in)</span>
              <strong>{samenvatting.zonder_volume}</strong>
            </div>
          )}
        </div>
      )}

      <section className="opslag-product-section">
        <h2>Gevolg per product (zelfde volume &amp; voorraad)</h2>
        <p className="opslag-help">
          Per product: welke liter-groep en welk tarief geldt in elke tabel, en wat dat betekent voor <strong>totale opslag / maand</strong>.
        </p>
        <div className="table-container opslag-compare-scroll">
          <table className="data-table opslag-product-table opslag-compare-table">
            <thead>
              <tr>
                <th rowSpan={2}>Product</th>
                <th rowSpan={2}>SKU</th>
                <th rowSpan={2}>L</th>
                <th colSpan={3} className="opslag-th-tarief-1">
                  {label1}
                </th>
                <th colSpan={3} className="opslag-th-tarief-2">
                  {label2}
                </th>
                <th rowSpan={2}>Lagere maand</th>
                <th rowSpan={2}>Verschil / mnd</th>
              </tr>
              <tr>
                <th className="opslag-th-tarief-1">Groep</th>
                <th className="opslag-th-tarief-1">€/dag tot.</th>
                <th className="opslag-th-tarief-1">€/mnd</th>
                <th className="opslag-th-tarief-2">Groep</th>
                <th className="opslag-th-tarief-2">€/wk tot.</th>
                <th className="opslag-th-tarief-2">€/mnd</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="empty-state">
                    Geen producten gevonden
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => {
                  const t1 = row.opslag_tarief_1 || row.opslag_lvb || row.opslag || {};
                  const t2 = row.opslag_tarief_2 || row.opslag_pick || {};
                  const v = row.vergelijking || {};
                  const t1Win = v.goedkoopste === 'tarief_1';
                  const t2Win = v.goedkoopste === 'tarief_2';
                  return (
                    <tr key={row.id} className={t1.inhoud_liter == null ? 'opslag-row-missing' : ''}>
                      <td>{row.naam}</td>
                      <td>{row.sku || '–'}</td>
                      <td>{t1.inhoud_liter != null ? t1.inhoud_liter.toFixed(2) : '–'}</td>
                      <td className={t1Win ? 'opslag-cell-cheaper' : ''} title={t1.tier_range || ''}>
                        {t1.tariefgroep || '–'}
                      </td>
                      <td className={t1Win ? 'opslag-cell-cheaper' : ''}>{formatEuro(t1.opslag_per_dag_totaal)}</td>
                      <td className={t1Win ? 'opslag-cell-cheaper' : ''}>{formatEuro2(t1.opslag_per_maand_totaal)}</td>
                      <td className={t2Win ? 'opslag-cell-cheaper' : ''} title={t2.tier_range || ''}>
                        {t2.tariefgroep || '–'}
                      </td>
                      <td className={t2Win ? 'opslag-cell-cheaper' : ''}>{formatEuro2(t2.opslag_per_week_totaal)}</td>
                      <td className={t2Win ? 'opslag-cell-cheaper' : ''}>{formatEuro2(t2.opslag_per_maand_totaal)}</td>
                      <td>
                        <span
                          className={`opslag-badge opslag-badge-${v.goedkoopste === 'tarief_1' ? 'tarief-1' : v.goedkoopste === 'tarief_2' ? 'tarief-2' : v.goedkoopste || 'none'}`}
                        >
                          {vergelijkingLabel(v)}
                        </span>
                      </td>
                      <td>{v.goedkoopste === 'gelijk' ? '—' : formatEuro2(v.verschil_maand)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default ProductenOpslagTab;
